import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { MaskedInput } from "../../../components/ui/masked-input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Mail, CheckCircle, UserPlus, Shield, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useRegister } from "@/hooks/useAuth";
import { useForm } from "react-hook-form";
import { registerSchema, type RegisterFormData } from "@/schemas/authSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useState } from "react";
import PrefLogo from "@/assets/logo_municipal.svg";

export const Register = () => {
  const navigate = useNavigate();
  const registerMutation = useRegister();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const {
    register: registerField,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    const { confirmPassword, ...dataToSend } = data;

    const cleanData = {
      ...dataToSend,
      cpf: dataToSend.cpf.replace(/\D/g, ""),
      phone: dataToSend.phone.replace(/\D/g, ""),
      zip_code: dataToSend.zip_code.replace(/\D/g, ""),
    };

    registerMutation.mutate(cleanData, {
      onSuccess: () => {
        setRegisteredEmail(data.email);
        setShowSuccessModal(true);
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          "Erro ao realizar cadastro. Tente novamente.";
        toast.error(errorMessage);
      },
    });
  };

  const searchAddress = async (cep: string) => {
    const cleanCEP = cep.replace(/\D/g, "");
    if (cleanCEP.length === 8) {
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cleanCEP}/json/`,
        );
        const data = await response.json();

        if (!data.erro) {
          setValue("street", data.logradouro || "");
          setValue("neighborhood", data.bairro || "");
          setValue("city", data.localidade || "");
          setValue("state", data.uf || "");
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate("/login");
  };

  return (
    <div className="min-h-screen w-full flex flex-col relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10 py-8">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-start relative z-10 flex-1 my-8">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                Faça parte da transformação da nossa cidade
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <UserPlus className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Cadastro Rápido
                  </h3>
                  <p className="text-sm text-gray-600">
                    Crie sua conta em poucos passos e comece a participar
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Users className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Participe Ativamente
                  </h3>
                  <p className="text-sm text-gray-600">
                    Contribua para melhorias e acompanhe demandas urbanas
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Dados Protegidos
                  </h3>
                  <p className="text-sm text-gray-600">
                    Suas informações são mantidas seguras e privadas
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Formulário */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-6">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={PrefLogo}
                    alt="Logo Municipal"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              <CardTitle className="text-3xl font-bold text-center text-gray-900">
                Crie sua conta
              </CardTitle>
              <p className="text-center text-muted-foreground text-sm">
                Preencha os dados abaixo para começar
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Dados Pessoais */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                    Dados Pessoais
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nome Completo</Label>
                    <Input
                      {...registerField("full_name")}
                      id="full_name"
                      type="text"
                      placeholder="João da Silva"
                      error={errors.full_name?.message}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cpf">CPF</Label>
                      <MaskedInput
                        {...registerField("cpf")}
                        mask="000.000.000-00"
                        id="cpf"
                        placeholder="000.000.000-00"
                        error={errors.cpf?.message}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <MaskedInput
                        {...registerField("phone")}
                        mask="(00) 00000-0000"
                        id="phone"
                        placeholder="(00) 00000-0000"
                        error={errors.phone?.message}
                      />
                    </div>
                  </div>
                </div>

                {/* Dados de Acesso */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                    Dados de Acesso
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      {...registerField("email")}
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      error={errors.email?.message}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Senha</Label>
                      <Input
                        {...registerField("password")}
                        id="password"
                        placeholder="••••••••"
                        showPasswordToggle
                        error={errors.password?.message}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                      <Input
                        {...registerField("confirmPassword")}
                        id="confirmPassword"
                        placeholder="••••••••"
                        showPasswordToggle
                        error={errors.confirmPassword?.message}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Mínimo de 6 caracteres
                  </p>
                </div>

                {/* Endereço */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                    Endereço
                  </h3>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-1">
                      <Label htmlFor="zip_code">CEP</Label>
                      <MaskedInput
                        {...registerField("zip_code", {
                          onBlur: (e) => searchAddress(e.target.value),
                        })}
                        mask="00000-000"
                        id="zip_code"
                        placeholder="00000-000"
                        error={errors.zip_code?.message}
                      />
                    </div>

                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="street">Rua</Label>
                      <Input
                        {...registerField("street")}
                        id="street"
                        type="text"
                        placeholder="Rua das Flores"
                        error={errors.street?.message}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2 col-span-1">
                      <Label htmlFor="number">Número</Label>
                      <Input
                        {...registerField("number")}
                        id="number"
                        type="text"
                        placeholder="123"
                        error={errors.number?.message}
                      />
                    </div>

                    <div className="space-y-2 col-span-3">
                      <Label htmlFor="neighborhood">Bairro</Label>
                      <Input
                        {...registerField("neighborhood")}
                        id="neighborhood"
                        type="text"
                        placeholder="Centro"
                        error={errors.neighborhood?.message}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2 col-span-3">
                      <Label htmlFor="city">Cidade</Label>
                      <Input
                        {...registerField("city")}
                        id="city"
                        type="text"
                        placeholder="São Paulo"
                        error={errors.city?.message}
                      />
                    </div>

                    <div className="space-y-2 col-span-1">
                      <Label htmlFor="state">Estado</Label>
                      <Input
                        {...registerField("state")}
                        id="state"
                        type="text"
                        placeholder="SP"
                        maxLength={2}
                        error={errors.state?.message}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Criando conta...
                    </div>
                  ) : (
                    "Criar Conta"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Já tem uma conta?{" "}
                  <Link
                    to="/login"
                    className="text-[hsl(var(--primary))] font-semibold hover:underline"
                  >
                    Faça login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      <Dialog
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        showCloseButton={false}
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center mb-4 shadow-lg">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            Conta Criada com Sucesso!
          </h3>

          <DialogContent>
            <div className="space-y-3 text-center">
              <p className="text-gray-600">
                Sua conta foi criada com sucesso, mas antes de fazer login, você
                precisa confirmar seu e-mail.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Mail className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    E-mail de confirmação enviado
                  </p>
                  <p className="text-xs text-blue-700">
                    Enviamos um link de confirmação para{" "}
                    <span className="font-semibold">{registeredEmail}</span>.
                    Verifique sua caixa de entrada e spam.
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-500">
                Após confirmar seu e-mail, você poderá fazer login normalmente.
              </p>
            </div>
          </DialogContent>

          <DialogFooter>
            <Button
              onClick={handleModalClose}
              className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
            >
              OK, Entendi
            </Button>
          </DialogFooter>
        </div>
      </Dialog>

      {/* Footer */}
      <div className="w-full text-center py-6 relative z-10">
        <p className="text-sm text-gray-600">
          © 2026 Vida Mais Simples - Frecheirinha, CE
        </p>
      </div>
    </div>
  );
};
